import { authFetch } from '../../../api/csrf';
import React, { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import PermissionGate from '../../../components/common/PermissionGate';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Search,
  Eye,
  Edit, Trash2,
  XCircle,
  FileText,
  Upload,
  X,
  Calendar,
  TrendingUp,
  CheckCircle,
  Loader2,
  Download,
  ArrowUpDown,
  ChevronUp,
  ChevronDown,
  Mail
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import offerService from '../../../api/services/offerService';
import jobService from '../../../api/services/jobService';
import locationService from '../../../api/services/locationService';
import candidateService from '../../../api/services/candidateService';
import { API_BASE_URL } from '../../../api/config';
import { post, del } from '../../../api/apiClient';
import CreateOfferModal, { availableBenefits, offerTypes } from '../../../components/recruitment/CreateOfferModal';
import CustomConfirm from '../../../components/common/CustomConfirm';
import { useCustomConfirm } from '../../../hooks/useCustomAlert';
import toast from 'react-hot-toast';
import { formatDate } from '../../../utils/stringUtils';
import { validateField, ErrorMessage } from '../../../utils/formValidation';
import ExportModal from '../../../components/common/ExportModal';
import SelectedExportFormatBar from '../../../components/common/SelectedExportFormatBar';
import PaginationControls from '../../../components/common/Pagination/PaginationControls';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import { formatIndianNumber } from '../../../utils/amountFormatter';

const OfferManagement = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [searchInputValue, setSearchInputValue] = useState(searchParams.get('search') || '');
  const [filterStatus, setFilterStatus] = useState(searchParams.get('status') || 'all');
  const [dateRange, setDateRange] = useState({
    startDate: searchParams.get('startDate') || '',
    endDate: searchParams.get('endDate') || ''
  });
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [showOfferDetails, setShowOfferDetails] = useState(false);
  const [showCreateOffer, setShowCreateOffer] = useState(false);
  const [filterErrors, setFilterErrors] = useState({});
  const [page, setPage] = useState(Number(searchParams.get('page')) || 1);
  const [pageSize, setPageSize] = useState(Number(searchParams.get('limit')) || 10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [serverTotalsKnown, setServerTotalsKnown] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [sendingOfferId, setSendingOfferId] = useState(null);
  const [editingOffer, setEditingOffer] = useState(null);
  const [isFetchingOffer, setIsFetchingOffer] = useState(false);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] =
    useState(false);
  const [inlineFormatPanelEntered, setInlineFormatPanelEntered] =
    useState(false);
  const selectedExportWrapRef = useRef(null);

  // Bulk delete state
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ 
    key: searchParams.get('sortBy') || null, 
    direction: searchParams.get('sortOrder') || 'asc' 
  });
  const searchInputRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const isInitialMountRef = useRef(true);

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <ArrowUpDown className="h-3 w-3 ml-1 text-gray-400 group-hover:text-gray-600 transition-colors" />;
    }
    return sortConfig.direction === 'asc' ?
      <ChevronUp className="h-3 w-3 ml-1 text-agrosaf-green" /> :
      <ChevronDown className="h-3 w-3 ml-1 text-agrosaf-green" />;
  };

  // Debounced search function
  const debouncedSearch = useCallback((inputValue) => {
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout
    searchTimeoutRef.current = setTimeout(() => {

      // Update the actual search term
      setSearchTerm(inputValue);

      // Reset to first page on search
      setPage(1);
    }, 300);
  }, []);

  const { confirmState, showConfirm, hideConfirm, isLoading: isConfirmLoading, setIsLoading: setIsConfirmLoading } = useCustomConfirm();

  // React Query: Load offer stats for KPI calculation
  const { data: kpiData = { totalOffers: 0, acceptedOffers: 0, pendingOffers: 0, rejectedOffers: 0, sentOffers: 0, acceptanceRate: 0, totalValue: 0 } } = useQuery({
    queryKey: ['offers', 'stats', 'kpi'],
    queryFn: async () => {
      const result = await offerService.getOfferStats();
      if (result && result.responseCode === 200 && result.data) {
        return result.data;
      }
      return { totalOffers: 0, acceptedOffers: 0, pendingOffers: 0, rejectedOffers: 0, sentOffers: 0, acceptanceRate: 0, totalValue: 0 };
    },
    staleTime: 60000,
    retry: 2,
  });

  // React Query: Load offers with filters
  // Validate date range before calling API
  const dateRangeValid = !(dateRange.startDate && dateRange.endDate && new Date(dateRange.endDate) < new Date(dateRange.startDate));

  const {
    data: offersData,
    isLoading: loadingOffers,
    isFetching: isFetchingOffers,
    error: offersError,
    refetch: refetchOffers
  } = useQuery({
    queryKey: ['offers', page, pageSize, filterStatus, searchTerm, dateRange.startDate, dateRange.endDate],
    queryFn: async () => {
      // Validate date range
      if (!dateRangeValid) {
        throw new Error('To Date must be after From Date');
      }

      const result = await offerService.getOffers({
        page,
        limit: pageSize,
        status: filterStatus === 'all' ? undefined : filterStatus,
        search: searchTerm || undefined,
        fromDate: dateRange.startDate || undefined,
        toDate: dateRange.endDate || undefined
      });

      // Extract offers and pagination
      let offersData = [];
      let paginationData = {};

      // Try multiple response structures
      if (result && result.offers && Array.isArray(result.offers)) {
        offersData = result.offers;
        paginationData = result.pagination || { totalItems: offersData.length, totalPages: 1 };
      } else if (result && result.responseCode === 200 && result.data) {
        // Check if offers are in result.data.offers
        if (result.data.offers && Array.isArray(result.data.offers)) {
          offersData = result.data.offers;
          paginationData = result.data.pagination || result.pagination || {};
        }
        // Check if data is directly an array
        else if (Array.isArray(result.data)) {
          offersData = result.data;
          paginationData = result.pagination || { totalItems: offersData.length, totalPages: 1 };
        }
        // Check if pagination is at root level
        else if (result.pagination) {
          offersData = result.data.offers || result.data || [];
          paginationData = result.pagination;
        }
      } else if (result && result.pagination) {
        // Pagination at root level
        offersData = result.offers || result.data || [];
        paginationData = result.pagination;
      } else {
        throw new Error(result?.responseMessage || 'Failed to load offers');
      }

      // Ensure pagination data has required fields
      if (!paginationData.totalPages && paginationData.totalItems) {
        paginationData.totalPages = Math.ceil(paginationData.totalItems / (paginationData.itemsPerPage || pageSize));
      }


      return { offers: offersData, pagination: paginationData };
    },
    enabled: dateRangeValid,
    placeholderData: (previousData) => previousData,
    staleTime: 0,
    retry: 2,
  });

  // Extract offers and pagination from query result
  const offers = offersData?.offers || [];
  const pagination = offersData?.pagination || {};
  useEffect(() => {
    // Check if pagination object exists and has data
    if (pagination && Object.keys(pagination).length > 0) {
      // Use API pagination values if available
      const apiTotalItems = pagination.totalItems;
      const apiTotalPages = pagination.totalPages;

      if (apiTotalItems !== undefined && typeof apiTotalItems === 'number' && apiTotalItems >= 0) {
        setTotalItems(apiTotalItems);
        // Prioritize API totalPages, fallback to calculation if not provided
        if (apiTotalPages !== undefined && typeof apiTotalPages === 'number' && apiTotalPages > 0) {
          setTotalPages(apiTotalPages);
        } else {
          // Calculate totalPages from totalItems and itemsPerPage or pageSize
          const itemsPerPage = pagination.itemsPerPage || pageSize;
          setTotalPages(Math.ceil(apiTotalItems / itemsPerPage) || 1);
        }
        setServerTotalsKnown(true);
      } else {
        // Fallback: use current offers length
        setTotalItems(offers.length);
        setTotalPages(Math.ceil(offers.length / pageSize) || 1);
        setServerTotalsKnown(false);
      }
    } else {
      // No pagination data - use current offers length
      setTotalItems(offers.length);
      setTotalPages(Math.ceil(offers.length / pageSize) || 1);
      setServerTotalsKnown(false);
    }
  }, [pagination, offers.length, pageSize]);

  // Update filter errors for date range
  useEffect(() => {
    if (!dateRangeValid && dateRange.startDate && dateRange.endDate) {
      setFilterErrors({ dateRange: 'To Date must be after From Date' });
    } else {
      setFilterErrors({});
    }
  }, [dateRangeValid, dateRange.startDate, dateRange.endDate]);

  // React Query: Load candidates eligible for offers (completed interviews without offers)
  const {
    data: eligibleCandidatesData,
    error: eligibleCandidatesError,
    refetch: refetchCandidates
  } = useQuery({
    queryKey: ['offers', 'eligible-candidates'],
    queryFn: async () => {
      const result = await offerService.getEligibleCandidates({ limit: 500 });

      if (Array.isArray(result)) {
        return result;
      }
      if (Array.isArray(result?.candidates)) {
        return result.candidates;
      }
      if (Array.isArray(result?.data?.candidates)) {
        return result.data.candidates;
      }

      return [];
    },
    staleTime: 0,
    retry: 2,
  });
  if (eligibleCandidatesError) {
  }
  const candidates = eligibleCandidatesData || [];

  // React Query: Load all jobs (active + inactive) so the modal can find any candidate's job.
  // Dropdown filtering (active-only in the UI) is handled inside CreateOfferModal.
  const { data: jobsData } = useQuery({
    queryKey: ['jobs', 'all'],
    queryFn: async () => {
      const result = await jobService.getJobs({
        page: 1,
        limit: 1000
      });

      if (result.responseCode === 200 || result.success) {
        const rawJobs = result.data?.jobs || result.data || [];

        // Transform jobs data to ensure proper ID mapping
        const transformedJobs = rawJobs.map(job => ({
          id: job.id || job._id,
          _id: job._id || job.id,
          title: job.title || job.jobTitle,
          department: job.department,
          location: job.location,
          status: job.status,
          isActive: job.isActive,
          ...job
        }));

        return transformedJobs;
      }
      return [];
    },
    staleTime: 0,
    retry: 2,
  });
  const allJobs = jobsData || [];
  // Only active jobs for the dropdown default list
  const jobs = allJobs.filter(job =>
    job.status === 'active' ||
    job.status === 'published' ||
    job.isActive === true
  );


  const { data: shiftsData, isLoading: loadingShifts } = useQuery({
    queryKey: ['shifts', 'offer-management'],
    queryFn: async () => {
      try {
        const baseUrl = API_BASE_URL;
        const response = await authFetch(`${baseUrl}/shifts-test`, {
        credentials: 'include',
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            }
        });

        if (response.ok) {
          const result = await response.json();

          let shifts = [];
          if (Array.isArray(result?.data?.shifts)) {
            shifts = result.data.shifts;
          } else if (Array.isArray(result?.data)) {
            shifts = result.data;
          } else if (Array.isArray(result?.shifts)) {
            shifts = result.shifts;
          } else if (Array.isArray(result)) {
            shifts = result;
          }

          const activeShifts = shifts.filter((shift) =>
            String(shift.isActive ?? shift.status)?.toLowerCase() === 'active' || shift.isActive === true
          );
          return activeShifts;
        }
        return [];
      } catch (error) {
        return [];
      }
    },
    staleTime: 0,
    retry: 2,
  });
  const shifts = shiftsData || [];

  // Load templates (mock for now)
  const loadTemplates = () => {
    const mockTemplates = [
      {
        id: 'standard',
        name: 'Standard Offer',
        department: 'All',
        salaryRange: '500000-1500000',
        benefits: ['Health Insurance', 'Provident Fund', 'Annual Bonus'],
        content: `We are pleased to offer you the position of {jobTitle} at ${process.env.REACT_APP_COMPANY_NAME}...`,
        isActive: true
      },
      {
        id: 'engineering',
        name: 'Engineering Offer',
        department: 'Engineering',
        salaryRange: '800000-1500000',
        benefits: ['Health Insurance', 'Provident Fund', 'Annual Bonus', 'Professional Development'],
        content: `We are pleased to offer you the position of {jobTitle} at ${process.env.REACT_APP_COMPANY_NAME}...`,
        isActive: true
      },
      {
        id: 'hr',
        name: 'HR Offer',
        department: 'Human Resources',
        salaryRange: '600000-1200000',
        benefits: ['Health Insurance', 'Provident Fund', 'Annual Bonus', 'Professional Development'],
        content: `We are pleased to offer you the position of {jobTitle} at ${process.env.REACT_APP_COMPANY_NAME}...`,
        isActive: true
      }
    ];
    setTemplates(mockTemplates);
  };

  // React Query: Load locations for dropdown
  const { data: locationsData, isLoading: loadingLocations } = useQuery({
    queryKey: ['locations', 'active'],
    queryFn: async () => {
      const result = await locationService.getActiveLocations();

      if (result.success) {
        const locationNames = result.data.map(loc => loc.name || loc.label || loc.value);
        return locationNames;
      } else {
        // Fallback to hardcoded locations
        return ['Mumbai', 'Delhi', 'Bangalore', 'Chennai', 'Remote'];
      }
    },
    staleTime: 0,
    retry: 2,
    onError: () => {
      // Fallback to hardcoded locations on error
      return ['Mumbai', 'Delhi', 'Bangalore', 'Chennai', 'Remote'];
    },
  });
  const locations = locationsData || ['Mumbai', 'Delhi', 'Bangalore', 'Chennai', 'Remote'];

  // Load templates on component mount (mock data)
  useEffect(() => {
    loadTemplates();
  }, []);

  // Autofocus search input on component mount
  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  // Debounced search effect - React Query will automatically refetch when queryKey changes
  useEffect(() => {
    // Debounce the search input value
    debouncedSearch(searchInputValue);

    // Cleanup function to clear timeout on unmount or when searchInputValue changes
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchInputValue, debouncedSearch]);

  // Sync filter state to URL search params
  useLayoutEffect(() => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);

      // Managed keys for this component
      const managedKeys = ['search', 'status', 'startDate', 'endDate', 'page', 'limit', 'sortBy', 'sortOrder'];
      managedKeys.forEach(key => params.delete(key));

      if (searchTerm) params.set('search', searchTerm);
      if (filterStatus && filterStatus !== 'all') params.set('status', filterStatus);
      if (dateRange.startDate) params.set('startDate', dateRange.startDate);
      if (dateRange.endDate) params.set('endDate', dateRange.endDate);
      if (page && page !== 1) params.set('page', String(page));
      if (pageSize && pageSize !== 10) params.set('limit', String(pageSize));
      if (sortConfig.key) params.set('sortBy', sortConfig.key);
      if (sortConfig.direction && sortConfig.direction !== 'asc') params.set('sortOrder', sortConfig.direction);

      return params;
    }, { replace: true });
  }, [searchTerm, filterStatus, dateRange, page, pageSize, sortConfig, setSearchParams]);

  // Reset to first page on filter or date range change
  useEffect(() => {
    // Skip page reset during initial mount to preserve pagination from URL
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return;
    }
    setPage(1);
    setSelectedRows(new Set()); // Clear selection when filters change
  }, [filterStatus, dateRange.startDate, dateRange.endDate, searchTerm]);

  // Reset selection on page change
  useEffect(() => {
    setSelectedRows(new Set());
  }, [page, pageSize]);

  // Row selection handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = filteredOffers.map(offer => offer.id);
      setSelectedRows(new Set(allIds));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (id) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  // React Query Mutation: Create offer
  const createOfferMutation = useMutation({
    mutationFn: async (formDataToUse) => {

      // 1. Find candidate
      const selectedCandidate = candidates.find(c =>
        (c.id && c.id.toString() === formDataToUse.candidateId?.toString()) ||
        (c._id && c._id.toString() === formDataToUse.candidateId?.toString())
      );

      // 2. Get the job — use _selectedJob passed from the modal (most reliable),
      //    then fall back to searching allJobs, then build from candidate data.
      let selectedJob =
        formDataToUse._selectedJob ||
        allJobs.find(j =>
          (j.id && j.id.toString() === formDataToUse.jobId?.toString()) ||
          (j._id && j._id.toString() === formDataToUse.jobId?.toString())
        ) ||
        null;

      // Final fallback: reconstruct a minimal job from the candidate's stored data
      if (!selectedJob && selectedCandidate && formDataToUse.jobId) {
        selectedJob = {
          _id: formDataToUse.jobId,
          id: formDataToUse.jobId,
          title: selectedCandidate.jobTitle || '',
          department: selectedCandidate.department || selectedCandidate.jobDepartment || '',
          location: '',
          status: 'closed',
        };
      }

      const selectedTemplate = templates.find(t => t.id === formDataToUse.templateId);

      if (!selectedCandidate) {
        throw new Error('Please select a valid candidate');
      }

      if (!selectedJob) {
        throw new Error('Please select a valid job');
      }


      const selectedShift = shifts.find((shift) => {
        const shiftId = shift.id || shift._id || shift.uuid;
        return shiftId === formDataToUse.shiftId;
      });

      // Handle documents: split between already uploaded (Cloudinary) and local files (if any)
      let uploadedDocuments = [];
      const alreadyUploaded = (formDataToUse.documents || []).filter(doc => doc.url || doc.publicId);
      const toUpload = (formDataToUse.documents || []).filter(doc => doc.file);

      uploadedDocuments = [...alreadyUploaded];

      if (toUpload.length > 0) {
        const uploadFormData = new FormData();
        toUpload.forEach((doc) => {
          if (doc.file) {
            uploadFormData.append('documents', doc.file);
          }
        });

        const uploadResult = await offerService.uploadDocuments(uploadFormData);
        const newlyUploaded = uploadResult.data?.documents || [];
        uploadedDocuments = [...uploadedDocuments, ...newlyUploaded];

        if (uploadedDocuments.length === 0) {
          throw new Error('Documents selected but failed to process');
        }
      }

      // Use _id from selected candidate/job, prioritizing _id for API
      const candidateId = selectedCandidate._id || selectedCandidate.id;
      const jobId = selectedJob._id || selectedJob.id;

      const parsedSalary = Number(String(formDataToUse.salary || '').replace(/,/g, ''));
      if (!Number.isFinite(parsedSalary) || parsedSalary < 1 || parsedSalary > 10000000) {
        throw new Error('Salary (Annual CTC) must be between 1 and 10000000');
      }

      const offerData = {
        candidate: candidateId,
        job: jobId,
        offerType: formDataToUse.offerType,
        jobTitle: selectedJob.title || 'Unknown Position',
        department: selectedJob.department || 'Not specified',
        location: formDataToUse.location || selectedJob.location || 'Not specified',
        startDate: formDataToUse.startDate,
        salary: parsedSalary,
        shiftId: formDataToUse.shiftId || undefined,
        shift: selectedShift ? {
          id: selectedShift.id || selectedShift._id || selectedShift.uuid,
          name: selectedShift.name,
          startTime: selectedShift.startTime,
          endTime: selectedShift.endTime
        } : undefined,
        benefits: formDataToUse.benefits.length > 0 ? formDataToUse.benefits : (selectedTemplate?.benefits || []), // Removed ['Health Insurance', 'Provident Fund']
        probationPeriod: formDataToUse.probationPeriod ? parseInt(formDataToUse.probationPeriod) : undefined, // Removed || 6
        noticePeriod: (() => {
          // Convert dropdown value to number (days)
          const noticePeriodValue = formDataToUse.noticePeriod;
          if (!noticePeriodValue) return undefined; // Removed return 30
          if (noticePeriodValue === 'immediate') return 0;
          if (noticePeriodValue === '15-days') return 15;
          if (noticePeriodValue === '30-days') return 30;
          if (noticePeriodValue === '60-days') return 60;
          if (noticePeriodValue === '90-days') return 90;
          // Fallback: try to parse as number
          // Commented out hardcoded default 30 days - use only data provided by admin
          return noticePeriodValue ? parseInt(noticePeriodValue) : undefined; // Removed || 30
        })(),
        status: 'draft',
        expiryDate: formDataToUse.expiryDate || undefined,
        templateId: formDataToUse.templateId,
        templateName: selectedTemplate?.name,
        documents: uploadedDocuments.map(({ id, isUploading, ...doc }) => doc),
        terms: {
          workingHours: formDataToUse.workingHours
        }
      };

      // Validate required fields
      const requiredFields = ['candidate', 'job', 'offerType', 'jobTitle', 'department', 'location', 'startDate', 'salary'];
      const missingFields = requiredFields.filter(field => {
        const value = offerData[field];
        return value === undefined || value === null || value === '' || (typeof value === 'string' && value.trim() === '');
      });

      if (missingFields.length > 0) {
        throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
      }

      const result = await offerService.createOffer(offerData);

      // Check if result is successful - handle various response structures
      // The apiClient interceptor returns response.data directly
      // If we got here without an exception, the API call succeeded (HTTP 201/200)
      // We just need to verify the response structure
      const responseCode = result?.responseCode;

      // Normalize responseCode to number for comparison
      const normalizedResponseCode = typeof responseCode === 'string' ? parseInt(responseCode, 10) : responseCode;

      // Success conditions: 
      // 1. responseCode is 201 or 200 (as number or string)
      // 2. success flag is true
      // 3. If no responseCode but we got here without exception, treat as success (API returned 201)
      const isSuccess = normalizedResponseCode === 201 ||
        normalizedResponseCode === 200 ||
        responseCode === '201' ||
        responseCode === '200' ||
        result?.success === true ||
        (!responseCode && result !== undefined && result !== null); // If no responseCode but we have data, assume success

      if (isSuccess) {
        return result;
      } else {
        const errorMessage = result?.responseMessage ||
          result?.message ||
          result?.error ||
          'Failed to create offer';
        throw new Error(errorMessage);
      }
    },
    onSuccess: async (result, variables) => {
      toast.success('Offer created successfully! You can now send the offer email to the candidate.');

      const candidateIdForStageUpdate =
        variables?.candidateId ||
        variables?.candidate ||
        variables?.selectedCandidate?._id ||
        variables?.selectedCandidate?.id;

      if (candidateIdForStageUpdate) {
        try {
          await candidateService.moveCandidate(candidateIdForStageUpdate, 'offer');
        } catch (stageError) {
        }
      }

      // Close modal immediately
      setShowCreateOffer(false);

      // Invalidate and refetch offers queries
      queryClient.invalidateQueries({ queryKey: ['offers'] });
    },
    onError: (error) => {
      const errorMessage = error.message || 'Unknown error occurred';

      // If it's a duplicate offer error, show the message directly (it's already descriptive)
      if (errorMessage.includes('already exists') || errorMessage.includes('duplicate')) {
        toast.error(errorMessage);
      } else {
        toast.error(`Error creating offer: ${errorMessage}`);
      }
    },
  });

  // React Query Mutation: Update existing offer
  const updateOfferMutation = useMutation({
    mutationFn: async ({ offerId, ...formDataToUse }) => {

      // Find selected candidate and job details
      const selectedCandidate = candidates.find(c =>
        (c.id && c.id.toString() === formDataToUse.candidateId?.toString()) ||
        (c._id && c._id.toString() === formDataToUse.candidateId?.toString())
      );
      // Get job — use _selectedJob from modal payload first, then search allJobs, then fallback
      const selectedJob =
        formDataToUse._selectedJob ||
        allJobs.find(j =>
          (j.id && j.id.toString() === formDataToUse.jobId?.toString()) ||
          (j._id && j._id.toString() === formDataToUse.jobId?.toString())
        ) ||
        (formDataToUse.jobId ? {
          _id: formDataToUse.jobId,
          id: formDataToUse.jobId,
          title: selectedCandidate?.jobTitle || '',
          department: selectedCandidate?.department || selectedCandidate?.jobDepartment || '',
          location: '',
          status: 'closed',
        } : null);
      const selectedTemplate = templates.find(t => t.id === formDataToUse.templateId);

      const selectedShift = shifts.find((shift) => {
        const shiftId = shift.id || shift._id || shift.uuid;
        return shiftId === formDataToUse.shiftId;
      });

      // Handle documents: split between already uploaded (Cloudinary) and local files (if any)
      let uploadedDocuments = [];
      const alreadyUploaded = (formDataToUse.documents || []).filter(doc => doc.url || doc.publicId);
      const toUpload = (formDataToUse.documents || []).filter(doc => doc.file);

      uploadedDocuments = [...alreadyUploaded];

      if (toUpload.length > 0) {
        const uploadFormData = new FormData();
        toUpload.forEach((doc) => {
          if (doc.file) {
            uploadFormData.append('documents', doc.file);
          }
        });

        const uploadResult = await offerService.uploadDocuments(uploadFormData);
        const newlyUploaded = uploadResult.data?.documents || [];
        uploadedDocuments = [...uploadedDocuments, ...newlyUploaded];

        if (uploadedDocuments.length === 0) {
          throw new Error('Documents selected but failed to process');
        }
      }

      // Use _id from selected candidate/job, prioritizing _id for API
      const candidateId = selectedCandidate ? (selectedCandidate._id || selectedCandidate.id) : formDataToUse.candidateId;
      const jobId = selectedJob ? (selectedJob._id || selectedJob.id) : formDataToUse.jobId;

      const parsedSalary = Number(String(formDataToUse.salary || '').replace(/,/g, ''));
      if (!Number.isFinite(parsedSalary) || parsedSalary < 1 || parsedSalary > 10000000) {
        throw new Error('Salary (Annual CTC) must be between 1 and 10000000');
      }

      const offerData = {
        candidate: candidateId,
        job: jobId,
        offerType: formDataToUse.offerType,
        jobTitle: selectedJob ? (selectedJob.title || 'Unknown Position') : 'Unknown Position',
        department: selectedJob ? (selectedJob.department || 'Not specified') : 'Not specified',
        location: formDataToUse.location || (selectedJob ? selectedJob.location : '') || 'Not specified',
        startDate: formDataToUse.startDate,
        salary: parsedSalary,
        shiftId: formDataToUse.shiftId || undefined,
        shift: selectedShift ? {
          id: selectedShift.id || selectedShift._id || selectedShift.uuid,
          name: selectedShift.name,
          startTime: selectedShift.startTime,
          endTime: selectedShift.endTime
        } : undefined,
        benefits: formDataToUse.benefits && formDataToUse.benefits.length > 0 ? formDataToUse.benefits : (selectedTemplate?.benefits || []),
        probationPeriod: formDataToUse.probationPeriod ? parseInt(formDataToUse.probationPeriod) : undefined,
        noticePeriod: (() => {
          // Convert dropdown value to number (days)
          const noticePeriodValue = formDataToUse.noticePeriod;
          if (!noticePeriodValue) return undefined;
          if (noticePeriodValue === 'immediate') return 0;
          if (noticePeriodValue === '15-days') return 15;
          if (noticePeriodValue === '30-days') return 30;
          if (noticePeriodValue === '60-days') return 60;
          if (noticePeriodValue === '90-days') return 90;
          return noticePeriodValue ? parseInt(noticePeriodValue) : undefined;
        })(),
        expiryDate: formDataToUse.expiryDate || undefined,
        templateId: formDataToUse.templateId,
        templateName: selectedTemplate?.name,
        documents: uploadedDocuments.map(({ id, isUploading, ...doc }) => doc),
        terms: {
          workingHours: formDataToUse.workingHours
        }
      };

      const result = await offerService.updateOffer(offerId, offerData);
      if (result.responseCode === 200 || result.success) {
        return result;
      } else {
        throw new Error(result.responseMessage || result.message || 'Failed to update offer');
      }
    },
    onSuccess: () => {
      toast.success('Offer updated successfully');
      setShowCreateOffer(false);
      setEditingOffer(null);
      queryClient.invalidateQueries({ queryKey: ['offers'] });
    },
    onError: (error) => {
      toast.error('Error updating offer: ' + error.message);
    },
  });

  const handleEditOffer = async (offerId) => {
    try {
      setIsFetchingOffer(true);
      const result = await offerService.getOfferById(offerId);

      let fullOfferData = null;
      if (result && (result.responseCode === 200 || result.success) && result.data) {
        fullOfferData = result.data;
      } else if (result && (result.id || result._id)) {
        fullOfferData = result;
      }

      if (fullOfferData) {
        // Normalize IDs and nested data for CreateOfferModal
        const normalizedData = {
          ...fullOfferData,
          id: fullOfferData.id || fullOfferData._id,
          candidateId: fullOfferData.candidateId || fullOfferData.candidate?._id || fullOfferData.candidate?.id || fullOfferData.candidate,
          jobId: fullOfferData.jobId || fullOfferData.job?._id || fullOfferData.job?.id || fullOfferData.job,
          shiftId: (fullOfferData.shiftId || fullOfferData.shift?._id || fullOfferData.shift?.id || '').toString(),
          workingHours: fullOfferData.workingHours || fullOfferData.terms?.workingHours || '',
          leavePolicy: fullOfferData.leavePolicy || fullOfferData.terms?.leavePolicy || ''
        };
        setEditingOffer(normalizedData);
        setShowCreateOffer(true);
      } else {
        toast.error('Failed to fetch offer details');
      }
    } catch (error) {
      toast.error('Error loading offer details: ' + (error.message || 'Unknown error'));
    } finally {
      setIsFetchingOffer(false);
    }
  };

  const handleSubmit = useCallback((formDataToUse) => {
    if (editingOffer) {
      updateOfferMutation.mutate({ offerId: editingOffer.id || editingOffer._id, ...formDataToUse });
    } else {
      createOfferMutation.mutate(formDataToUse);
    }
  }, [createOfferMutation, updateOfferMutation, editingOffer]);

  // React Query Mutation: Delete offer
  const deleteOfferMutation = useMutation({
    mutationFn: async (offerId) => {
      const result = await offerService.deleteOffer(offerId);
      if (result && (result.responseCode === 200 || result.success === true || result.responseMessage?.includes('successfully'))) {
        return result;
      } else if (result && result.responseCode === 404) {
        // Treat 404 as success (already deleted)
        return result;
      } else {
        throw new Error(result?.responseMessage || result?.message || 'Failed to delete offer');
      }
    },
    onSuccess: () => {
      toast.success('Offer deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['offers'] });
    },
    onError: (error) => {
      toast.error('Error deleting offer: ' + error.message);
    },
  });

  const handleDelete = (offerId) => {
    showConfirm({
      title: 'Delete Offer',
      message: 'Are you sure you want to delete this offer? This action cannot be undone.',
      type: 'danger',
      confirmText: 'Delete',
      onConfirm: () => {
        hideConfirm();
        deleteOfferMutation.mutate(offerId);
      }
    });
  };

  // Bulk delete handler
  const handleBulkDelete = async () => {
    if (selectedRows.size === 0) return;
    setBulkDeleteLoading(true);
    const ids = Array.from(selectedRows);
    let successCount = 0;
    let failCount = 0;
    for (const id of ids) {
      try {
        const result = await offerService.deleteOffer(id);
        if (result && (result.responseCode === 200 || result.success === true ||
          result.responseMessage?.includes('successfully') || result.responseCode === 404)) {
          successCount++;
        } else {
          failCount++;
        }
      } catch {
        failCount++;
      }
    }
    setBulkDeleteLoading(false);
    setShowBulkDeleteConfirm(false);
    setSelectedRows(new Set());
    if (successCount > 0) {
      toast.success(`${successCount} offer(s) deleted successfully${failCount > 0 ? `, ${failCount} failed` : ''}`);
      queryClient.invalidateQueries({ queryKey: ['offers'] });
    } else {
      toast.error('Failed to delete selected offers');
    }
  };

  // React Query Mutation: Send offer email
  const sendOfferMutation = useMutation({
    mutationFn: async ({ offerId, isResend }) => {
      const result = await offerService.sendOfferEmail(offerId, isResend);
      const isSuccess = (result && result.responseCode === 200) ||
        (result && result.success === true) ||
        (result && result.data && result.data.offerId);
      if (!isSuccess) {
        throw new Error(result?.responseMessage || result?.message || 'Failed to send offer');
      }
      return result;
    },
    onMutate: ({ offerId }) => {
      setSendingOfferId(offerId);
    },
    onSuccess: () => {
      toast.success('Offer email sent successfully to candidate!');
      queryClient.invalidateQueries({ queryKey: ['offers'] });
    },
    onError: (error) => {
      toast.error('Error sending offer: ' + error.message);
    },
    onSettled: () => {
      setSendingOfferId(null);
    },
  });

  const handleSendOffer = (offerId) => {
    if (sendOfferMutation.isPending) {
      return;
    }
    const offer = offers.find(o => o.id === offerId);
    const isResend = offer?.status === 'sent';
    sendOfferMutation.mutate({ offerId, isResend });
  };

  // React Query Mutation: Accept offer
  const acceptOfferMutation = useMutation({
    mutationFn: async (offerId) => {
      const result = await offerService.acceptOffer(offerId);
      if (result.responseCode === 200 || result.success) {
        return result;
      } else {
        throw new Error(result.responseMessage || result.message || 'Failed to accept offer');
      }
    },
    onSuccess: () => {
      toast.success('Offer accepted successfully');
      queryClient.invalidateQueries({ queryKey: ['offers'] });
    },
    onError: (error) => {
      toast.error('Error accepting offer: ' + error.message);
    },
  });

  const handleAcceptOffer = (offerId) => {
    acceptOfferMutation.mutate(offerId);
  };

  // React Query Mutation: Reject offer
  const rejectOfferMutation = useMutation({
    mutationFn: async (offerId) => {
      const result = await offerService.rejectOffer(offerId);
      if (result.responseCode === 200 || result.success) {
        return result;
      } else {
        throw new Error(result.responseMessage || result.message || 'Failed to reject offer');
      }
    },
    onSuccess: () => {
      toast.success('Offer rejected successfully');
      queryClient.invalidateQueries({ queryKey: ['offers'] });
    },
    onError: (error) => {
      toast.error('Error rejecting offer: ' + error.message);
    },
  });

  const handleRejectOffer = (offerId) => {
    rejectOfferMutation.mutate(offerId);
  };

  // Implement frontend sorting

  const filteredOffers = React.useMemo(() => {
    let sortableOffers = [...offers];
    if (sortConfig.key !== null) {
      sortableOffers.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle nested or complex fields if necessary
        if (sortConfig.key === 'candidate') {
          aValue = a.candidateName || '';
          bValue = b.candidateName || '';
        } else if (sortConfig.key === 'job') {
          aValue = a.jobTitle || '';
          bValue = b.jobTitle || '';
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableOffers;
  }, [offers, sortConfig]);

  const baseExportFilters = useMemo(() => {
    const f = {};
    const trimmed = searchTerm?.trim();
    if (trimmed) f.search = trimmed;
    if (filterStatus !== 'all') f.status = filterStatus;
    if (dateRange.startDate) f.startDate = dateRange.startDate;
    if (dateRange.endDate) f.endDate = dateRange.endDate;
    return f;
  }, [searchTerm, filterStatus, dateRange.startDate, dateRange.endDate]);

  const exportSortBy = useMemo(() => {
    if (!sortConfig.key) return 'createdAt';
    if (sortConfig.key === 'candidate') return 'createdAt';
    if (sortConfig.key === 'job') return 'jobTitle';
    return sortConfig.key;
  }, [sortConfig.key]);

  const exportSortOrder = sortConfig.key ? sortConfig.direction : 'desc';

  const requestBackendExport = useCallback(
    async ({
      scope,
      format,
      selectedIds = [],
      overrideFilters = null,
      overridePage = null,
      overrideLimit = null,
    }) => {
      const response = await authFetch(
        `${process.env.REACT_APP_API_BASE_URL}/exports/generate`, {
        credentials: 'include',
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
                      },
          body: JSON.stringify({
            module: 'offer-management',
            format,
            scope,
            filters: overrideFilters ?? baseExportFilters,
            page: overridePage ?? page,
            limit: overrideLimit ?? pageSize,
            sortBy: exportSortBy,
            sortOrder: exportSortOrder,
            selectedIds,
          }),
        }
      );

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Export request failed');
      }

      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const filenameMatch =
        disposition.match(/filename\*=UTF-8''([^;]+)/i) ||
        disposition.match(/filename="?([^"]+)"?/i);
      const fallbackExt = format === 'xlsx' ? 'xlsx' : format;
      const fallbackName = `offer-management-export.${fallbackExt}`;
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, '')
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [baseExportFilters, page, pageSize, exportSortBy, exportSortOrder]
  );

  const handleOfferSelectedFormatExport = useCallback(
    async (format) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
      const ids = Array.from(selectedRows);
      if (!ids.length) {
        toast.error('No selected offers to export.');
        return;
      }
      setExportLoading(true);
      try {
        await requestBackendExport({
          scope: 'selected',
          format,
          selectedIds: ids,
        });
        toast.success(`${ids.length} offer(s) exported!`);
        setShowInlineSelectedFormats(false);
      } catch (error) {
        toast.error(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [selectedRows, requestBackendExport]
  );

  const handleOfferScopedExport = useCallback(
    async (scope, format, meta = {}) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) {
        return;
      }

      setExportLoading(true);
      try {
        if (scope === 'currentPage' && !filteredOffers.length) {
          toast.error('No offers to export on the current page.');
          return;
        }

        if (scope === 'filtered' && totalItems === 0) {
          toast.error('No offers match the current filters.');
          return;
        }

        if (scope === 'dateRange') {
          const startDate = meta?.startDate;
          const endDate = meta?.endDate;
          if (!startDate || !endDate) {
            toast.error('Please select start and end dates.');
            return;
          }
          await requestBackendExport({
            scope: 'dateRange',
            format,
            overrideFilters: {
              ...baseExportFilters,
              startDate,
              endDate,
            },
          });
        } else if (scope === 'all') {
          await requestBackendExport({
            scope: 'all',
            format,
            overrideFilters: {},
            overridePage: 1,
            overrideLimit: 1000,
          });
        } else if (scope === 'filtered') {
          await requestBackendExport({
            scope: 'filtered',
            format,
          });
        } else if (scope === 'currentPage') {
          await requestBackendExport({
            scope: 'currentPage',
            format,
          });
        } else {
          return;
        }

        toast.success('Export downloaded.');
        setShowExportModal(false);
      } catch (error) {
        toast.error(error?.message || 'Export failed. Please try again.');
      } finally {
        setExportLoading(false);
      }
    },
    [filteredOffers.length, totalItems, baseExportFilters, requestBackendExport]
  );

  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      setShowInlineSelectedFormats((open) => !open);
      return;
    }
    setShowInlineSelectedFormats(false);
    setShowExportModal(true);
  };

  useEffect(() => {
    if (selectedRows.size === 0) {
      setShowInlineSelectedFormats(false);
    }
  }, [selectedRows]);

  useEffect(() => {
    if (!showInlineSelectedFormats) {
      setInlineFormatPanelEntered(false);
      return;
    }
    const id = requestAnimationFrame(() =>
      requestAnimationFrame(() => setInlineFormatPanelEntered(true))
    );
    return () => cancelAnimationFrame(id);
  }, [showInlineSelectedFormats]);

  useEffect(() => {
    if (!showInlineSelectedFormats) return;
    const onMouseDown = (e) => {
      if (
        selectedExportWrapRef.current &&
        !selectedExportWrapRef.current.contains(e.target)
      ) {
        setShowInlineSelectedFormats(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [showInlineSelectedFormats]);

  const hasPrevPage = page > 1;
  const hasKnownTotal = serverTotalsKnown && Number.isFinite(totalItems) && totalItems > 0;
  // Enable Next if either server indicates more pages OR the current page is full (optimistic next)
  const hasNextPage = (page < totalPages) || (offers.length === pageSize);

  // Debug logs removed to prevent console spam

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'sent': return 'bg-blue-100 text-blue-800';
      case 'accepted': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'expired': return 'bg-orange-100 text-orange-800';
      case 'withdrawn': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatCurrency = (amount, currency) => {
    const formatter = new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency === 'INR' ? 'INR' : currency === 'USD' ? 'USD' : 'EUR',
      minimumFractionDigits: 0
    });
    return formatter.format(amount);
  };


  const OfferDetailsModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Offer Details</h2>
            <button
              onClick={() => setShowOfferDetails(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <XCircle className="h-6 w-6" />
            </button>
          </div>

          {selectedOffer && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Candidate Information</h3>
                  <div className="space-y-2">
                    <p><span className="font-medium">Name:</span> {selectedOffer.candidateName}</p>
                    <p><span className="font-medium">Email:</span> {selectedOffer.candidateEmail}</p>
                    <p><span className="font-medium">Phone:</span> {selectedOffer.candidatePhone}</p>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Job Information</h3>
                  <div className="space-y-2">
                    <p><span className="font-medium">Position:</span> {selectedOffer.jobTitle}</p>
                    <p><span className="font-medium">Department:</span> {selectedOffer.jobDepartment}</p>
                    <p><span className="font-medium">Location:</span> {selectedOffer.location}</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Offer Details</h3>
                  <div className="space-y-2">
                    <p><span className="font-medium">Offer ID:</span> {selectedOffer.offerId}</p>
                    <p><span className="font-medium">Type:</span> {selectedOffer.offerType}</p>
                    <p><span className="font-medium">Salary:</span> {formatCurrency(selectedOffer.salary, selectedOffer.currency || 'INR')}</p>
                    <p><span className="font-medium">Start Date:</span> {selectedOffer.startDate}</p>
                    <p><span className="font-medium">Expiry Date:</span> {selectedOffer.expiryDate}</p>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Status & Timeline</h3>
                  <div className="space-y-2">
                    <p><span className="font-medium">Status:</span>
                      <span className={`ml-2 px-2 py-1 rounded-full text-xs ${getStatusColor(selectedOffer.status)}`}>
                        {selectedOffer.status}
                      </span>
                    </p>
                    <p><span className="font-medium">Sent Date:</span> {selectedOffer.sentDate || 'Not sent'}</p>
                    <p><span className="font-medium">Created By:</span> {selectedOffer.createdBy}</p>
                  </div>
                </div>
              </div>

              {/* Terms & Conditions */}
              {selectedOffer.terms && (selectedOffer.terms.workingHours || selectedOffer.terms.leavePolicy) && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Terms & Conditions</h3>
                  <div className="space-y-3">
                    {selectedOffer.terms.workingHours && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Working Hours</label>
                        <p className="text-gray-900">{selectedOffer.terms.workingHours}</p>
                      </div>
                    )}
                    {selectedOffer.terms.leavePolicy && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Leave Policy</label>
                        <p className="text-gray-900">{selectedOffer.terms.leavePolicy}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  onClick={() => setShowOfferDetails(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Close
                </button>
                {selectedOffer.status === 'draft' && (
                  <PermissionGate permission="recruitment" action="update">
                    <button
                      onClick={() => handleSendOffer(selectedOffer.id)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Send Offer
                    </button>
                  </PermissionGate>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (loadingOffers && !offersData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-agrosaf-green"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Offer Management</h1>
          <p className="text-gray-600 mt-1">Manage job offers and candidate communications</p>
        </div>
        <div className="flex items-center space-x-3">
          {selectedRows.size > 0 && (
            <PermissionGate permission="recruitment" action="delete">
              <Button
                variant="danger"
                icon={<Trash2 className="h-4 w-4" />}
                onClick={() => setShowBulkDeleteConfirm(true)}
                loading={bulkDeleteLoading}
              >
                Delete Selected ({selectedRows.size})
              </Button>
            </PermissionGate>
          )}
          <PermissionGate permission="recruitment" action="read">
            <div className="relative shrink-0" ref={selectedExportWrapRef}>
              <Button
                variant="outline"
                icon={exportLoading ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-green-600 border-t-transparent" /> : <Download className="h-4 w-4" />}
                onClick={handleExportClick}
                disabled={exportLoading}
                className={selectedRows.size > 0 ? "bg-green-50 border-green-200 text-green-600" : ""}
              >
                {selectedRows.size > 0 ? `Export Selected (${selectedRows.size})` : "Export"}
              </Button>
              <SelectedExportFormatBar
                show={selectedRows.size > 0 && showInlineSelectedFormats}
                panelEntered={inlineFormatPanelEntered}
                disabled={exportLoading}
                onSelect={handleOfferSelectedFormatExport}
              />
            </div>
          </PermissionGate>
          <PermissionGate permission="recruitment" action="create">
            <Button
              onClick={() => setShowCreateOffer(true)}
              className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Offer
            </Button>
          </PermissionGate>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FileText className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Offers</p>
              <p className="text-2xl font-bold text-gray-900">{formatIndianNumber(kpiData.totalOffers)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Accepted</p>
              <p className="text-2xl font-bold text-gray-900">{formatIndianNumber(kpiData.acceptedOffers)}</p>
            </div>
          </div>
        </div>



        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <TrendingUp className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Acceptance Rate</p>
              <p className="text-2xl font-bold text-gray-900">{kpiData.acceptanceRate}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex flex-wrap items-end gap-4 w-full">
          <div className="relative flex-[2] min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search by candidate name, email, job title or department"
              value={searchInputValue}
              maxLength={100}
              onKeyPress={(e) => {
                // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                if (!/^[a-zA-Z0-9\s@._+-]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              onChange={(e) => {
                let value = e.target.value;
                // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                value = value.replace(/[^a-zA-Z0-9\s@._+-]/g, '');
                // Enforce max length
                if (value.length > 100) {
                  value = value.substring(0, 100);
                }
                setSearchInputValue(value);
                // Debouncing is handled by useEffect
              }}
              onFocus={() => {
                // Ensure focus is maintained
                if (searchInputRef.current) {
                  searchInputRef.current.focus();
                }
              }}
              autoFocus
              className="pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent w-full"
            />
            {searchInputValue && (
              <button
                onClick={() => {
                  setSearchInputValue('');
                  // Maintain focus after clearing
                  setTimeout(() => {
                    if (searchInputRef.current) {
                      searchInputRef.current.focus();
                    }
                  }, 50);
                }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 hover:text-gray-600 transition-colors"
                title="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <div className="flex-[0.8] min-w-[150px]">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="draft">Draft</option>
              <option value="sent">Sent</option>
              <option value="accepted">Accepted</option>
              <option value="rejected">Rejected</option>
              <option value="expired">Expired</option>
            </select>
          </div>
          <div className="flex-shrink-0">
            <DateRangeFilter
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              onChange={setDateRange}
            />
          </div>

          {(searchInputValue || searchTerm || filterStatus !== 'all' || dateRange.startDate || dateRange.endDate) && (
            <div className="relative">
              <label className="block text-xs font-medium text-gray-700 mb-1 opacity-0">Clear</label>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSearchInputValue('');
                  setFilterStatus('all');
                  setDateRange({ startDate: '', endDate: '' });
                  setFilterErrors({});
                  queryClient.invalidateQueries({ queryKey: ['offers'] });
                }}
                className="w-full px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center space-x-1 whitespace-nowrap"
              >
                <X className="h-4 w-4" />
                <span>Clear</span>
              </button>
            </div>
          )}
        </div>
        {filterErrors.dateRange && (
          <div className="mt-2 text-sm text-red-600">{filterErrors.dateRange}</div>
        )}
      </div>

      {/* Error State */}
      {
        offersError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-600">{offersError.message || 'Failed to load offers'}</p>
            <button
              onClick={() => refetchOffers()}
              className="mt-2 text-red-600 hover:text-red-800 underline"
            >
              Try Again
            </button>
          </div>
        )
      }

      {/* Offers Table */}
      {
        filteredOffers.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No offers found
              </h3>
              <p className="text-gray-500 mb-4">
                {searchInputValue || searchTerm || filterStatus !== 'all' || dateRange.startDate || dateRange.endDate
                  ? 'Try adjusting your search or filter criteria.'
                  : 'Get started by creating your first offer.'}
              </p>
              {!searchInputValue && !searchTerm && filterStatus === 'all' && !dateRange.startDate && !dateRange.endDate && (
                <PermissionGate permission="recruitment" action="create">
                  <Button
                    variant="primary"
                    icon={<Plus className="h-4 w-4" />}
                    onClick={() => setShowCreateOffer(true)}
                    className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
                  >
                    Create Offer
                  </Button>
                </PermissionGate>
              )}
            </div>
          </div>
        ) : (
          <div className={`bg-white rounded-lg border border-gray-200 overflow-hidden transition-opacity duration-150 ${isFetchingOffers ? 'opacity-70' : 'opacity-100'}`}>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 table-fixed">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left w-10">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-green-600 focus:ring-green-500 h-4 w-4"
                        checked={filteredOffers.length > 0 && selectedRows.size === filteredOffers.length}
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[220px] cursor-pointer group"
                      onClick={() => requestSort('candidate')}
                    >
                      <div className="flex items-center">
                        Candidate
                        {getSortIcon('candidate')}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[220px] cursor-pointer group"
                      onClick={() => requestSort('job')}
                    >
                      <div className="flex items-center">
                        Job Position
                        {getSortIcon('job')}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer group"
                      onClick={() => requestSort('salary')}
                    >
                      <div className="flex items-center">
                        Salary
                        {getSortIcon('salary')}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer group"
                      onClick={() => requestSort('status')}
                    >
                      <div className="flex items-center">
                        Status
                        {getSortIcon('status')}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer group"
                      onClick={() => requestSort('createdAt')}
                    >
                      <div className="flex items-center">
                        Created
                        {getSortIcon('createdAt')}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredOffers.map((offer) => {
                    const isSendingThisOffer = sendOfferMutation.isPending && sendingOfferId === offer.id;
                    return (
                      <tr key={offer.id} className={`hover:bg-gray-50 ${selectedRows.has(offer.id) ? 'bg-green-50/50' : ''}`}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            className="rounded border-gray-300 text-green-600 focus:ring-green-500 h-4 w-4"
                            checked={selectedRows.has(offer.id)}
                            onChange={() => handleSelectRow(offer.id)}
                          />
                        </td>
                        <td className="px-6 py-4 max-w-[220px]">
                          <div className="max-w-full overflow-hidden">
                            <div className="text-sm font-medium text-gray-900 truncate capitalize">{offer.candidateName}</div>
                            <div className="text-sm text-gray-500 truncate">{offer.candidateEmail}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 max-w-[220px]">
                          <div className="max-w-full overflow-hidden">
                            <div className="text-sm text-gray-900 truncate capitalize">{offer.jobTitle}</div>
                            <div className="text-sm text-gray-500 truncate capitalize">{offer.jobDepartment}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatCurrency(offer.salary, offer.currency || 'INR')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 rounded-full text-xs capitalize ${getStatusColor(offer.status)}`}>
                            {offer.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(offer.createdAt, { includeTime: true })}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            <PermissionGate permission="recruitment" action="read">
                              <button
                                onClick={() => navigate(`/view_offer/${offer.id}`)}
                                className="text-blue-600 hover:text-blue-900"
                                title="View Offer Details"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                            </PermissionGate>
                            {offer.status !== 'accepted' && (
                              <PermissionGate permission="recruitment" action="update">
                                <button
                                  onClick={() => handleEditOffer(offer.id || offer._id)}
                                  className={`text-indigo-600 hover:text-indigo-900 ${isFetchingOffer ? 'opacity-50 cursor-not-allowed' : ''}`}
                                  title="Edit Offer"
                                  disabled={isFetchingOffer}
                                >
                                  {isFetchingOffer && editingOffer?.id === offer.id ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Edit className="h-4 w-4" />
                                  )}
                                </button>
                              </PermissionGate>
                            )}
                            <PermissionGate permission="recruitment" action="delete">
                              <button
                                onClick={() => handleDelete(offer.id)}
                                className="text-red-600 hover:text-red-900"
                                title="Delete Offer"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </PermissionGate>
                            {(offer.status === 'draft' || offer.status === 'sent') && (
                              <PermissionGate permission="recruitment" action="update">
                                <button
                                  onClick={() => handleSendOffer(offer.id)}
                                  className={`text-green-600 hover:text-green-900 ${isSendingThisOffer ? 'opacity-50 cursor-not-allowed' : ''}`}
                                  title={isSendingThisOffer ? 'Sending offer...' : (offer.status === 'sent' ? 'Resend Offer' : 'Send Offer')}
                                  disabled={isSendingThisOffer}
                                  aria-busy={isSendingThisOffer}
                                >
                                  {isSendingThisOffer ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Mail className="h-4 w-4" />
                                  )}
                                </button>
                              </PermissionGate>
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            {totalPages > 0 && totalItems > 0 && (
              <PaginationControls
                currentPage={page}
                totalPages={totalPages}
                totalItems={totalItems}
                limit={pageSize}
                hasNextPage={hasNextPage}
                hasPrevPage={hasPrevPage}
                onPageChange={(p) => setPage(p)}
                onLimitChange={(l) => { setPageSize(Number(l)); setPage(1); }}
              />
            )}
          </div>
        )
      }

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4 !mt-0">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
                <Trash2 className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                Delete Selected Offers
              </h3>
              <p className="text-gray-600 text-center mb-2">
                You are about to permanently delete{' '}
                <span className="font-semibold text-red-600">{selectedRows.size} offer(s)</span>.
              </p>
              <p className="text-sm text-gray-500 text-center mb-6">
                This action cannot be undone.
              </p>
              <div className="flex space-x-3">
                <Button
                  variant="secondary"
                  onClick={() => setShowBulkDeleteConfirm(false)}
                  className="flex-1"
                  disabled={bulkDeleteLoading}
                >
                  Cancel
                </Button>
                <PermissionGate permission="recruitment" action="delete">
                  <Button
                    variant="danger"
                    onClick={handleBulkDelete}
                    className="flex-1"
                    loading={bulkDeleteLoading}
                  >
                    Delete {selectedRows.size} Offer(s)
                  </Button>
                </PermissionGate>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {
        showCreateOffer && (
          <CreateOfferModal
            isOpen={showCreateOffer}
            onClose={() => {
              setShowCreateOffer(false);
              setEditingOffer(null);
            }}
            onSubmit={handleSubmit}
            initialData={editingOffer}
            lockInitialCandidate={!!editingOffer}
            candidates={candidates}
            jobs={allJobs}
            templates={templates}
            shifts={shifts}
            loadingShifts={loadingShifts}
            isSubmitting={createOfferMutation.isPending || updateOfferMutation.isPending}
            error={createOfferMutation.error?.message || updateOfferMutation.error?.message}
            onRefreshCandidates={refetchCandidates}
            locations={locations}
            loadingLocations={loadingLocations}
          />
        )
      }
      {showOfferDetails && <OfferDetailsModal />}

      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        loading={exportLoading}
        title="Export Offers"
        showDateRange
        scopedFormatExport={{
          onExport: handleOfferScopedExport,
          formats: ['csv', 'xlsx', 'pdf'],
          showCurrentPage: true,
          showFiltered: true,
          showDateRange: true,
          showAll: true,
        }}
      />

      <CustomConfirm
        isOpen={confirmState.isOpen}
        onClose={hideConfirm}
        onCancel={hideConfirm}
        onConfirm={confirmState.onConfirm}
        title={confirmState.title}
        message={confirmState.message}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
        type={confirmState.type}
        isLoading={isConfirmLoading}
      />
    </div >
  );
};

export default OfferManagement;